import React, { useState, useEffect, useCallback, useContext } from "react";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  format,
} from "date-fns";
import { ptBR } from "date-fns/locale";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";

// Cores por status do agendamento (chip no dia + rótulo no dialog).
const STATUS_STYLES = {
  scheduled: { bg: "#e3f2fd", fg: "#1565c0", label: "Agendado" },
  done: { bg: "#e8f5e9", fg: "#2e7d32", label: "Concluído" },
  cancelled: {
    bg: "#eeeeee",
    fg: "#757575",
    label: "Cancelado",
    strike: true,
  },
};

const CrmCalendar = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin" || user?.profile === "owner";

  // Embed (aba do ERP): preenche a altura toda (padrão CrmDashboard).
  const embed = sessionStorage.getItem("whaticketEmbed") === "1";

  const [currentMonth, setCurrentMonth] = useState(() =>
    startOfMonth(new Date()),
  );
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [sellerFilter, setSellerFilter] = useState("");
  const [queues, setQueues] = useState([]);
  const [queueFilter, setQueueFilter] = useState("");
  const [whatsApps, setWhatsApps] = useState([]);
  const [whatsappFilter, setWhatsappFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  // A grade visível inclui os dias das semanas vizinhas (células esmaecidas),
  // então o intervalo do fetch cobre a grade inteira, não só o mês.
  const gridStart = startOfWeek(currentMonth, { locale: ptBR });
  const gridEnd = endOfWeek(endOfMonth(currentMonth), { locale: ptBR });

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const from = startOfWeek(currentMonth, { locale: ptBR }).toISOString();
      const to = endOfWeek(endOfMonth(currentMonth), {
        locale: ptBR,
      }).toISOString();
      const { data } = await api.get("/crm/appointments", {
        params: { from, to },
      });
      setAppointments(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Admin/owner: lista de vendedores para o filtro (o backend já restringe o
  // usuário comum aos próprios agendamentos).
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/users");
        setSellers(data?.users || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [isAdmin]);

  // Fontes dos filtros de Funil (= Fila do ticket, vocabulário do dono) e
  // Conexão — para todos os papéis (o vendedor também filtra os próprios).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(Array.isArray(data) ? data : []);
      } catch (err) {
        setQueues([]);
      }
      try {
        const { data } = await api.get("/whatsapp/");
        setWhatsApps(Array.isArray(data) ? data : []);
      } catch (err) {
        setWhatsApps([]);
      }
    })();
  }, []);

  // Atualização ao vivo: qualquer evento de agendamento refaz o fetch.
  useEffect(() => {
    const socket = openSocket();
    socket.on("appointment", fetchAppointments);
    return () => {
      socket.disconnect();
    };
  }, [fetchAppointments]);

  const handleUpdateStatus = async (status) => {
    if (!selected) return;
    setUpdating(true);
    try {
      await api.put(`/crm/appointments/${selected.id}`, { status });
      setSelected(null);
      fetchAppointments();
    } catch (err) {
      toastError(err);
    } finally {
      setUpdating(false);
    }
  };

  const visibleAppointments = appointments.filter(
    (a) =>
      (!sellerFilter || String(a.userId) === String(sellerFilter)) &&
      (!queueFilter || String(a.ticket?.queueId) === String(queueFilter)) &&
      (!whatsappFilter ||
        String(a.ticket?.whatsappId) === String(whatsappFilter)),
  );

  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const weekDays = days.slice(0, 7);
  const today = new Date();

  const appointmentsOfDay = (day) =>
    visibleAppointments
      .filter((a) => isSameDay(new Date(a.scheduledAt), day))
      .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

  const selectedStatus = selected && STATUS_STYLES[selected.status];

  return (
    <div
      style={{
        height: embed ? "100%" : "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: 16,
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <Typography variant="h6" style={{ fontWeight: 700, marginRight: 8 }}>
          Agendamentos
        </Typography>
        <IconButton
          size="small"
          onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
          aria-label="Mês anterior"
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          variant="subtitle1"
          style={{ minWidth: 150, textAlign: "center" }}
        >
          {format(currentMonth, "MMMM 'de' yyyy", { locale: ptBR })}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
          aria-label="Próximo mês"
        >
          <ChevronRightIcon />
        </IconButton>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setCurrentMonth(startOfMonth(new Date()))}
        >
          Hoje
        </Button>
        {loading && <CircularProgress size={18} style={{ marginLeft: 4 }} />}

        <div style={{ flex: 1 }} />

        {queues.length > 0 && (
          <FormControl size="small" style={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={queueFilter}
              onChange={(e) => setQueueFilter(e.target.value)}
            >
              <MenuItem value="">Funil: todos</MenuItem>
              {queues.map((q) => (
                <MenuItem key={q.id} value={String(q.id)}>
                  {q.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {whatsApps.length > 1 && (
          <FormControl size="small" style={{ minWidth: 160 }}>
            <Select
              displayEmpty
              value={whatsappFilter}
              onChange={(e) => setWhatsappFilter(e.target.value)}
            >
              <MenuItem value="">Conexão: todas</MenuItem>
              {whatsApps.map((w) => (
                <MenuItem key={w.id} value={String(w.id)}>
                  {w.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isAdmin && (
          <FormControl size="small" style={{ minWidth: 180 }}>
            <Select
              displayEmpty
              value={sellerFilter}
              onChange={(e) => setSellerFilter(e.target.value)}
            >
              <MenuItem value="">Vendedor: todos</MenuItem>
              {sellers.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>

      {/* Grade mensal */}
      <Paper
        variant="outlined"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "auto",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            borderBottom: "1px solid rgba(0,0,0,0.12)",
          }}
        >
          {weekDays.map((d) => (
            <Typography
              key={d.toISOString()}
              variant="caption"
              style={{
                padding: "6px 8px",
                fontWeight: 700,
                textTransform: "capitalize",
                textAlign: "center",
                color: "#666",
              }}
            >
              {format(d, "EEE", { locale: ptBR })}
            </Typography>
          ))}
        </div>
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gridAutoRows: "minmax(96px, 1fr)",
          }}
        >
          {days.map((day) => {
            const inMonth = isSameMonth(day, currentMonth);
            const isToday = isSameDay(day, today);
            const dayAppointments = appointmentsOfDay(day);
            return (
              <div
                key={day.toISOString()}
                style={{
                  border: "1px solid rgba(0,0,0,0.06)",
                  padding: 4,
                  minWidth: 0,
                  overflow: "hidden",
                  background: inMonth ? "transparent" : "rgba(0,0,0,0.03)",
                  opacity: inMonth ? 1 : 0.5,
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    fontSize: 12,
                    fontWeight: isToday ? 700 : 400,
                    background: isToday ? "#1565c0" : "transparent",
                    color: isToday ? "#fff" : "inherit",
                    marginBottom: 2,
                  }}
                >
                  {format(day, "d")}
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  {dayAppointments.map((a) => {
                    const st =
                      STATUS_STYLES[a.status] || STATUS_STYLES.scheduled;
                    return (
                      <div
                        key={a.id}
                        onClick={() => setSelected(a)}
                        title={`${format(new Date(a.scheduledAt), "HH:mm")} ${
                          a.contact?.name || ""
                        }`}
                        style={{
                          cursor: "pointer",
                          background: st.bg,
                          color: st.fg,
                          borderRadius: 4,
                          padding: "1px 5px",
                          fontSize: 11,
                          lineHeight: "16px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textDecoration: st.strike ? "line-through" : "none",
                          fontWeight: 600,
                        }}
                      >
                        {format(new Date(a.scheduledAt), "HH:mm")}{" "}
                        {a.contact?.name || "Contato"}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Paper>

      {/* Detalhe do agendamento */}
      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="xs"
        fullWidth
      >
        {selected && (
          <>
            <DialogTitle>Agendamento</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" gutterBottom>
                <strong>Contato:</strong> {selected.contact?.name || "—"}
                {selected.contact?.number
                  ? ` (${selected.contact.number})`
                  : ""}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Vendedor:</strong> {selected.user?.name || "—"}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Data/hora:</strong>{" "}
                {format(
                  new Date(selected.scheduledAt),
                  "EEEE, d 'de' MMMM 'de' yyyy 'às' HH:mm",
                  { locale: ptBR },
                )}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Status:</strong>{" "}
                <span
                  style={{
                    color: selectedStatus?.fg,
                    fontWeight: 700,
                    textDecoration: selectedStatus?.strike
                      ? "line-through"
                      : "none",
                  }}
                >
                  {selectedStatus?.label || selected.status}
                </span>
              </Typography>
              {selected.notes && (
                <Typography
                  variant="body2"
                  style={{ whiteSpace: "pre-wrap", marginTop: 8 }}
                >
                  <strong>Notas:</strong> {selected.notes}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)} disabled={updating}>
                Fechar
              </Button>
              {selected.status === "scheduled" && (
                <>
                  <Button
                    onClick={() => handleUpdateStatus("cancelled")}
                    disabled={updating}
                    style={{ color: "#757575" }}
                  >
                    Cancelar agendamento
                  </Button>
                  <Button
                    onClick={() => handleUpdateStatus("done")}
                    disabled={updating}
                    variant="contained"
                    color="primary"
                  >
                    Concluir
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default CrmCalendar;
